<template>
  <div class="oauth-consent d-flex justify-content-center">
    <b-card class="oauth-consent-card mt-5" style="max-width: 540px; width: 100%;">
      <div class="text-center mb-4">
        <span class="material-symbols-outlined oauth-consent-icon">link</span>
        <h4 class="font-weight-bold mt-2 mb-0">Authorize access</h4>
      </div>

      <div v-if="loading" class="text-center py-4" data-testid="consent-loading">
        <b-spinner></b-spinner>
      </div>

      <b-alert v-else-if="error" show variant="danger" data-testid="consent-error">
        {{ error }}
      </b-alert>

      <template v-else>
        <p class="text-center">
          <b data-testid="consent-client-name">{{ client.name }}</b> wants to connect to your Exatom account
          <template v-if="$root.me"> ({{ $root.me.email }})</template>.
        </p>

        <div class="card card-body bg-light mb-4">
          <div class="small text-muted text-uppercase letter-spacing-1px mb-2">This will allow it to</div>
          <ul class="mb-0 pl-3" data-testid="consent-scopes">
            <li v-for="scope in scopes" :key="scope.id">{{ scope.description }}</li>
          </ul>
        </div>

        <p v-if="hasAgreedToAIConsent" class="small text-muted text-center" data-testid="consent-ai-already-agreed">
          <span class="material-symbols-outlined align-middle" style="font-size: 1rem;">check_circle</span>
          You've already agreed to the <a href="https://exatom.io/ai-terms/" target="_blank" rel="noopener">MCP AI Terms</a>.
        </p>

        <div class="d-flex justify-content-between">
          <b-btn variant="default" :disabled="submitting" @click="deny" data-testid="consent-deny">
            Cancel
          </b-btn>
          <ladda-btn
            :loading="submitting"
            data-style="expand-left"
            class="btn btn-primary"
            data-testid="consent-approve"
            @click.native="approve">
            Authorize
          </ladda-btn>
        </div>
      </template>
    </b-card>
  </div>
</template>

<script>
import LaddaBtn from '@/vendor/libs/ladda/Ladda'

const MCP_AI_CONSENT_VERSION = 'mcp_ai_consent_20260903'

export default {
  name: 'OAuthConsent',
  components: { LaddaBtn },
  metaInfo: {
    title: 'Authorize access'
  },
  data () {
    return {
      loading: true,
      submitting: false,
      error: null,
      client: { id: null, name: 'An application' },
      scopes: []
    }
  },
  computed: {
    hasAgreedToAIConsent () {
      return !!this.$hasAIConsent(MCP_AI_CONSENT_VERSION)
    }
  },
  async created () {
    // The browser is sent here by the API authorization endpoint. If the user
    // is not logged in, send them through login and return to this exact URL.
    if (!this.$api.getToken()) {
      window.location.href = '/login?redirect=' + encodeURIComponent(this.$route.fullPath)
      return
    }

    // Surface validation errors the API forwarded (e.g. unknown client).
    if (this.$route.query.error) {
      this.error = this.$route.query.error_description || this.$route.query.error
      this.loading = false
      return
    }

    await this.loadDetails()
  },
  methods: {
    async loadDetails () {
      this.loading = true
      try {
        const query = new URLSearchParams(this.oauthParams()).toString()
        const data = await this.$api.get('/oauth/authorize/details?' + query)
        this.client = data.client
        this.scopes = data.scopes || []
      } catch (e) {
        this.error = this.errorMessage(e, 'This authorization request is invalid or has expired.')
      } finally {
        this.loading = false
      }
    },
    async approve () {
      const consented = await this.$msgBoxAIConsent({
        version: MCP_AI_CONSENT_VERSION,
        title: 'MCP AI Consent',
        heading: 'By enabling MCP access, you accept the '
      })
      if (!consented) return

      await this.complete('/oauth/authorize/approve')
    },
    async deny () {
      await this.complete('/oauth/authorize/deny')
    },
    async complete (endpoint) {
      this.submitting = true
      try {
        const data = await this.$api.post(endpoint, this.oauthParams())
        if (data && data.redirect_uri) {
          window.location.href = data.redirect_uri
          return
        }
        this.error = 'The authorization server did not return a redirect.'
      } catch (e) {
        this.error = this.errorMessage(e, 'Could not complete the authorization request.')
      } finally {
        this.submitting = false
      }
    },
    // The OAuth parameters the API authorization endpoint forwarded in the URL.
    oauthParams () {
      const keys = [
        'response_type', 'client_id', 'redirect_uri', 'scope',
        'state', 'code_challenge', 'code_challenge_method', 'nonce'
      ]
      const params = {}
      keys.forEach((key) => {
        if (this.$route.query[key] !== undefined) {
          params[key] = this.$route.query[key]
        }
      })
      return params
    },
    errorMessage (e, fallback) {
      return e?.response?.data?.error_description || e?.response?.data?.message || fallback
    }
  }
}
</script>

<style scoped>
  .oauth-consent-icon {
    font-size: 2.5rem;
    color: var(--primary, #5a8dee);
  }
</style>
